"use client";

import { useState, useEffect, lazy, Suspense } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Flex, SimpleGrid, Alert, AlertIcon, AlertTitle, AlertDescription, Button, Spinner, Center } from "@chakra-ui/react";

import KpiCards from "./components/KpiCards";
import Table1 from "./components/Table1";
import Top10A from "./components/Top10A";
import Top10B from "./components/Top10B";
import Top10C from "./components/Top10C";
import Top10D from "./components/Top10D";
import Chart1 from "./echarts/Chart1"; // ECharts migration — old AmCharts version kept at ./amcharts/Chart1 for instant revert
import Chart2 from "./echarts/Chart2"; // ECharts migration — old AmCharts version kept at ./amcharts/Chart2 for instant revert
import Chart4 from "./echarts/Chart4"; // ECharts migration — old Chart.js version kept at ./components/Chart4 for instant revert
import Chart5 from "./echarts/Chart5"; // ECharts migration — old Chart.js version kept at ./components/Chart5 for instant revert
import Chart6 from "./echarts/Chart6"; // ECharts migration — old Chart.js version kept at ./components/Chart6 for instant revert
import Chart7 from "./echarts/Chart7"; // ECharts migration — old AmCharts version kept at ./amcharts/Chart7 for instant revert
import Chart8 from "./echarts/Chart8"; // ECharts migration — old AmCharts version kept at ./amcharts/Chart8 for instant revert
import Chart9 from "./echarts/Chart9"; // ECharts migration — old AmCharts version kept at ./amcharts/Chart9 for instant revert
import Chart10 from "./echarts/Chart10"; // ECharts migration — old AmCharts version kept at ./amcharts/Chart10 for instant revert
import ChkRegionTabs from "./components/ChkRegionTabs";

import { FaChartBar, FaMedal, FaChartArea, FaLayerGroup, FaLightbulb } from "react-icons/fa";

import Section, { Panel } from "../../components/shared/Section";
import { useSupplierRetailer } from "../../wrappers/SupplierRetailerContext";
import { usePageAccess } from "../../wrappers/PageAccessContext";
import { neutral, radius, elevation } from "../../theme/tokens";

// Map View (docs/michael/TASK-map-view.md) — lazy so Leaflet and the map code
// cost nothing until the first switch; after that it stays in the bundle cache
// and flipping views is instant.
const MapView = lazy(() => import("./MapView"));

const SECTIONS_KEY = "spearbi_sales_sections";

// Per-section identity — icon plus accent, matched to the secondary colour of
// each section's ECharts graphs. (salesTrends' bright amber is the one value
// below 4.5:1 on white — chosen deliberately to match its charts.)
const SECTION = {
  salesReport: { icon: FaChartBar, accent: "#0E7C86" },
  top10Stores: { icon: FaMedal, accent: "#2C6E9B" },
  salesTrends: { icon: FaChartArea, accent: "#E8940F" },
  additionalAnalytics: { icon: FaLayerGroup, accent: "#6B4E9B" },
  forecastInsights: { icon: FaLightbulb, accent: "#12775F" },
};

const DEFAULT_SECTIONS = {
  salesReport: true,
  top10Stores: true,
  salesTrends: true,
  additionalAnalytics: true,
  forecastInsights: true,
};

// Chart tiles: a fixed frame so a row of charts lines up regardless of what
// each library decides its intrinsic height is.
const chartTile = {
  bg: neutral[0],
  border: "1px solid",
  borderColor: neutral[200],
  borderRadius: radius.md,
  boxShadow: elevation.raised,
  p: 3,
  // 360, not 330. The month labels on these axes are rotated ~45°, so they
  // need real vertical room; at 330 with 12px padding the canvas had ~12px
  // less than the original 350/16 and the bottom of every label was clipped.
  height: "360px",
  minW: 0,
  overflow: "visible",
  css: {
    "& > div": { overflow: "visible !important" },
    "& canvas": { maxWidth: "none !important" },
  },
};

const Sales = () => {
  const { selectedRetailer, selectedSupplier } = useSupplierRetailer();
  const { restrictedyn, user_page_access } = usePageAccess();
  const [isAdmin, setIsAdmin] = useState(false);
  const [searchParams] = useSearchParams();
  // Map view: TopBar toggle sets ?view=map. Double-gated — the URL alone is not
  // enough without the MapView page grant (and the backend enforces it anyway).
  const mapMode = searchParams.get("view") === "map" && user_page_access.some((p) => p.value === "MapView");

  // Which sections you had open survives a reload — a report you never look at
  // shouldn't cost you a scroll past it on every visit.
  const [openSections, setOpenSections] = useState(() => {
    try {
      return { ...DEFAULT_SECTIONS, ...JSON.parse(localStorage.getItem(SECTIONS_KEY) || "{}") };
    } catch {
      return DEFAULT_SECTIONS;
    }
  });

  useEffect(() => {
    const userid = localStorage.getItem("userid");
    setIsAdmin(userid === "1" || userid === "2");
  }, []);

  const toggleSection = (section) =>
    setOpenSections((prev) => {
      const next = { ...prev, [section]: !prev[section] };
      localStorage.setItem(SECTIONS_KEY, JSON.stringify(next));
      return next;
    });

  const key = `${selectedSupplier}-${selectedRetailer}`;

  if (restrictedyn) {
    return (
      <Alert
        status="warning"
        variant="subtle"
        flexDirection="column"
        alignItems="center"
        textAlign="center"
        py={10}
        borderRadius={radius.md}
      >
        <AlertIcon boxSize="32px" mr={0} />
        <AlertTitle mt={3} mb={1} fontSize="lg">
          Account payment required
        </AlertTitle>
        <AlertDescription maxWidth="md">
          Settle your account fee to open the Sales report.
          <Box mt={4}>
            <Button onClick={() => (window.location.href = "/myProfile")}>Go to my profile</Button>
          </Box>
        </AlertDescription>
      </Alert>
    );
  }

  if (mapMode) {
    return (
      <Suspense
        fallback={
          <Center h="50vh">
            <Spinner size="lg" color="teal.500" />
          </Center>
        }
      >
        <MapView key={key} />
      </Suspense>
    );
  }

  return (
    <Box>
      <Box mb={6}>
        <KpiCards key={`kpi-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
      </Box>

      <Section
        title="Sales Report"
        icon={SECTION.salesReport.icon}
        accent={SECTION.salesReport.accent}
        description="Monthly units and value by article, with stock on hand"
        isOpen={openSections.salesReport}
        onToggle={() => toggleSection("salesReport")}
      >
        <Panel padded={false}>
          <Box maxHeight="640px" overflow="auto">
            <Table1 key={`tbl-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
          </Box>
        </Panel>
      </Section>

      <Section
        title="Top 10 Stores"
        icon={SECTION.top10Stores.icon}
        accent={SECTION.top10Stores.accent}
        description="Ranked by sales, stock, overstock and discontinued holdings"
        isOpen={openSections.top10Stores}
        onToggle={() => toggleSection("top10Stores")}
      >
        <SimpleGrid columns={{ base: 1, md: 2, xl: selectedRetailer === "PNP" ? 4 : 3 }} spacing={4}>
          <Panel>
            <Top10A key={`t1-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
          </Panel>
          <Panel>
            <Top10B key={`t2-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
          </Panel>
          <Panel>
            <Top10C key={`t3-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
          </Panel>
          {selectedRetailer === "PNP" && (
            <Panel>
              <Top10D key={`t4-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
            </Panel>
          )}
        </SimpleGrid>
      </Section>

      <Section
        title="Sales Trends"
        icon={SECTION.salesTrends.icon}
        accent={SECTION.salesTrends.accent}
        description="Where the value sits by store profile and region"
        isOpen={openSections.salesTrends}
        onToggle={() => toggleSection("salesTrends")}
      >
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          <Box {...chartTile}>
            <Chart1 key={`c1-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
          </Box>
          <Box {...chartTile}>
            <Chart2 key={`c2-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
          </Box>
        </SimpleGrid>
      </Section>

      <Section
        title="Additional Analytics"
        icon={SECTION.additionalAnalytics.icon}
        accent={SECTION.additionalAnalytics.accent}
        description="Monthly movement in value, units and year-on-year growth"
        isOpen={openSections.additionalAnalytics}
        onToggle={() => toggleSection("additionalAnalytics")}
      >
        {selectedRetailer === "CHK" ? (
          <ChkRegionTabs
            supplier={selectedSupplier}
            retailer={selectedRetailer}
            contentCardProps={chartTile}
            keyStr={key}
          />
        ) : (
          <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
            <Box {...chartTile}>
              <Chart4 supplier={selectedSupplier} retailer={selectedRetailer} />
            </Box>
            <Box {...chartTile}>
              <Chart5 supplier={selectedSupplier} retailer={selectedRetailer} />
            </Box>
            <Box {...chartTile}>
              <Chart6 supplier={selectedSupplier} retailer={selectedRetailer} />
            </Box>
          </SimpleGrid>
        )}
      </Section>

      <Section
        title="Forecast & Inventory"
        icon={SECTION.forecastInsights.icon}
        accent={SECTION.forecastInsights.accent}
        description="Sell-through, days cover, forecast accuracy and ABC mix"
        isOpen={openSections.forecastInsights}
        onToggle={() => toggleSection("forecastInsights")}
      >
        <Flex direction="column" gap={4}>
          {isAdmin && (
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
              <Box {...chartTile}>
                <Chart7 key={`c7-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
              </Box>
              <Box {...chartTile}>
                <Chart8 key={`c8-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
              </Box>
            </SimpleGrid>
          )}

          <SimpleGrid columns={{ base: 1, lg: isAdmin ? 2 : 1 }} spacing={4}>
            {isAdmin && (
              <Box {...chartTile}>
                <Chart9 key={`c9-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
              </Box>
            )}
            <Box {...chartTile} overflow="auto">
              <Chart10 key={`c10-${key}`} supplier={selectedSupplier} retailer={selectedRetailer} />
            </Box>
          </SimpleGrid>
        </Flex>
      </Section>
    </Box>
  );
};

export default Sales;
